import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Flame, ChevronDown, Palette, Scan } from 'lucide-react';
import { supabase } from '../supabase';

/**
 * SkinAnalysisCard — Wrapped-style carousel reveal.
 *
 * Demo-driven redesign (2026-06-04): replaces the legacy info-dense card
 * (skin tone box + skin type box + concerns box + recs) with three swipeable
 * "reveal" cards inside chat. Each card answers ONE question with one bold
 * headline. CTAs and trust detail live below the carousel in normal chat flow.
 *
 * Sofia's design constraints (locked):
 *   - photo is reference, not the moment; analysis IS the moment
 *   - confidence is hidden in the Trust & detail disclosure, not on every card
 *   - pure white cards, charcoal text, peach accents only
 *   - manual swipe (no auto-advance — too risky for live demo)
 *   - 3 reveal cards: tone → type → concerns. CTAs in chat below.
 *
 * Parsing is unchanged from the legacy card — same regexes, same data shapes —
 * so the backend response format and the journal-mirror code in ChatComponent
 * keep working identically. ONLY the render has changed.
 *
 * Props (back-compat + 2 new):
 *   analysis              — markdown blob from the AI (existing)
 *   imageUrl              — user's face photo URL (existing — now tiny corner thumb)
 *   userId                — for hasRoutine lookup (existing)
 *   onAddRoutine          — quiet text-link CTA when no routine (existing)
 *   isAnonymous           — gates routine CTA (existing)
 *   onAnalyseProduct      — NEW primary CTA — routes to product analysis flow
 *   onSeeShadeMatches     — NEW primary CTA — routes to shade match flow
 */
const SkinAnalysisCard = ({
  analysis,
  imageUrl,
  userId,
  onAddRoutine,
  isAnonymous = false,
  onAnalyseProduct,
  onSeeShadeMatches,
  onShowSignIn,
}) => {
  // ── hasRoutine lookup (unchanged from legacy) ───────────────────────────────
  // Recommendations are only meaningful once we know what the user uses, so a
  // first-time user gets a quiet CTA to add their routine instead of generic,
  // routine-blind advice.
  const [hasRoutine, setHasRoutine] = useState(null); // null = loading
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let uid = userId;
        if (!uid) {
          const { data: { user } } = await supabase.auth.getUser();
          uid = user?.id;
        }
        if (!uid) { if (!cancelled) setHasRoutine(false); return; }
        const { data } = await supabase.from('skin_routine_data').select('routines').eq('user_id', uid).single();
        const routines = Array.isArray(data?.routines) ? data.routines : [];
        const hasProduct = routines.some(r =>
          [...(r?.morningSteps || []), ...(r?.eveningSteps || []), ...(r?.steps || [])]
            .some(s => s && (s.productName || '').trim())
        );
        if (!cancelled) setHasRoutine(hasProduct);
      } catch {
        if (!cancelled) setHasRoutine(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // ── parsers (unchanged from legacy) ─────────────────────────────────────────
  const parseSections = (text) => {
    const sections = {
      skinTone: '', skinType: '', confidence: '', concerns: '',
      recommendations: '', disclaimer: '',
    };
    const parts = String(text || '').split(/## /g).filter(Boolean);
    parts.forEach(part => {
      const lines = part.trim().split('\n');
      const header = lines[0].toLowerCase();
      const content = lines.slice(1).join('\n').trim();
      if (header.includes('skin tone')) sections.skinTone = content;
      else if (header.includes('skin type')) sections.skinType = content;
      else if (header.includes('confidence')) sections.confidence = content;
      else if (header.includes('concern')) sections.concerns = content;
      else if (header.includes('recommendation')) sections.recommendations = content;
      else if (header.includes('disclaimer')) sections.disclaimer = content;
    });
    return sections;
  };

  const stripMarkdown = (text) => {
    if (!text) return text;
    return text.replace(/\*\*/g, '').replace(/^\*(.+)\*$/s, '$1');
  };

  const extractToneInfo = (text) => {
    const toneMatch = text.match(/(very[- ]deep|light[- ]medium|medium[- ]deep|fair|light|medium|tan|deep|rich)/i);
    const undertoneMatch = text.match(/(warm|cool|neutral|olive)/i);
    const titleCase = (str) => {
      if (!str) return '';
      return str.trim().replace(/\s+/g, '-').toLowerCase().split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join('-');
    };
    return {
      tone: toneMatch ? titleCase(toneMatch[1]) : null,
      undertone: undertoneMatch ? (undertoneMatch[1].charAt(0).toUpperCase() + undertoneMatch[1].slice(1).toLowerCase()) : null,
      description: stripMarkdown(text),
    };
  };

  const extractSkinType = (text) => {
    const typeMatch = text.match(/(oily|dry|combination|normal|sensitive)/i);
    const confidenceMatch = sections.confidence.match(/(\d+)%/);
    const characteristics = [];
    const lines = text.split('\n');
    lines.forEach(line => {
      const t = line.trim();
      if (!t ||
          t.toLowerCase().includes('characteristics') ||
          t.toLowerCase().includes('visible') ||
          t.toLowerCase().includes('then list')) return;
      if (t.startsWith('-') || t.startsWith('*') || t.startsWith('•')) {
        const cleaned = stripMarkdown(t.replace(/^[-*•]\s*/, '').trim());
        characteristics.push(cleaned);
      }
    });
    return {
      type: typeMatch ? typeMatch[1].charAt(0).toUpperCase() + typeMatch[1].slice(1) : null,
      confidence: confidenceMatch ? parseInt(confidenceMatch[1]) : null,
      characteristics,
    };
  };

  const parseConcerns = (text) => {
    if (text.toLowerCase().includes('no concerns detected') ||
        text.toLowerCase().includes('no major concerns') ||
        text.toLowerCase().includes('none detected')) return [];
    const concerns = [];
    const lines = text.split('\n');
    lines.forEach(line => {
      line = line.trim();
      if (!line || !line.startsWith('-') ||
          line.toLowerCase().includes('examples:') ||
          line.toLowerCase().includes('if none are visible')) return;
      const fmt1 = line.match(/^[-*•]\s*\*\*([^*]+)\*\*\s*\(Severity:\s*(LOW|MEDIUM|HIGH),\s*Confidence:\s*(\d+)%\):\s*(.+)/i);
      const fmt2 = !fmt1 && line.match(/^[-*•]\s*\*\*([^(]+?)\s*\(Severity:\s*(LOW|MEDIUM|HIGH),\s*Confidence:\s*(\d+)%\)\*\*\s*[:\-]?\s*(.+)/i);
      const m = fmt1 || fmt2;
      if (m) {
        concerns.push({ title: m[1].trim(), severity: m[2].toLowerCase(), confidence: parseInt(m[3]), description: m[4].trim() });
      } else {
        const cleaned = line.replace(/\(Severity:.*?%\)\**/gi, '').replace(/\*\*/g, '').trim();
        const sm = cleaned.match(/^[-*•]\s*(.+?):\s*(.+)/);
        if (sm) concerns.push({ title: sm[1].trim(), severity: 'low', confidence: null, description: sm[2].trim() });
      }
    });
    return concerns;
  };

  const sections = parseSections(analysis);
  const toneInfo = extractToneInfo(sections.skinTone);
  const skinTypeInfo = extractSkinType(sections.skinType);
  const concerns = parseConcerns(sections.concerns);

  // ── derived display values ──────────────────────────────────────────────────
  // First sentence of a longer prose paragraph — used as the one-liner under
  // the headline on each reveal card. Falls back to the full string if there's
  // no period (defensive). Strips any markdown that slipped through.
  const firstSentence = (s) => {
    if (!s) return '';
    const clean = stripMarkdown(s).trim();
    const m = clean.match(/^[^.!?]*[.!?]/);
    return (m ? m[0] : clean).trim();
  };

  // Bucket the % confidence into a label (Sofia 2026-06-04):
  //   > 60 → High,  40–60 → Medium,  < 40 → Low.
  // Per-section % undermines trust ("everything is 82%"); a single label
  // is more honest. Lives in Trust & detail, not on the cards.
  const confidenceLabel = (() => {
    const c = skinTypeInfo.confidence;
    if (c == null) return null;
    if (c > 60) return 'High';
    if (c >= 40) return 'Medium';
    return 'Low';
  })();

  // ── carousel state ──────────────────────────────────────────────────────────
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);
  const totalCards = 3;

  // Track which card is in view via scroll position. Snap + native horizontal
  // scroll handle the swipe; we just listen to scrollLeft to keep dots in sync.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const w = el.clientWidth;
      if (!w) return;
      const idx = Math.round(el.scrollLeft / w);
      setActiveIndex(Math.max(0, Math.min(totalCards - 1, idx)));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToCard = useCallback((i) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: el.clientWidth * i, behavior: 'smooth' });
  }, []);

  // Routine CTA tap handler (guards anonymous)
  const handleAddRoutine = () => {
    if (isAnonymous && onShowSignIn) { onShowSignIn(); return; }
    if (onAddRoutine) onAddRoutine();
  };

  // Tiny helper: render the small face-photo thumbnail in the top-right corner
  // of each card. Subtle, NOT the focal point — analysis is the moment.
  const CornerPhoto = () => imageUrl ? (
    <div className="absolute top-5 right-5 w-10 h-10 rounded-xl overflow-hidden ring-1 ring-black/5">
      <img src={imageUrl} alt="" className="w-full h-full object-cover" />
    </div>
  ) : null;

  // The Powered-by-Son:mi watermark on each card. Subtle.
  const Watermark = () => (
    <div className="flex items-center gap-1 text-[13px] uppercase tracking-widest text-[#2A324B]/30 font-medium">
      <Flame className="w-2.5 h-2.5 text-[#F3D5B5]" />
      <span>by Son:mi</span>
    </div>
  );

  // Severity dot colour for a concern pill
  const severityColor = (sev) =>
    sev === 'high' ? 'bg-red-400' :
    sev === 'medium' ? 'bg-amber-400' :
    'bg-emerald-400';

  // ── card components (inline) ────────────────────────────────────────────────
  // Each card is a flex-shrink-0 100%-wide column. Sized to feel tall and airy
  // (~440px min-height) so the headline gets room to breathe.

  const CardShell = ({ children, kicker }) => (
    <div className="snap-center flex-shrink-0 w-full px-1">
      <div className="relative bg-white rounded-3xl shadow-sm ring-1 ring-black/5 min-h-[440px] flex flex-col p-7 sm:p-8">
        <CornerPhoto />
        <p className="text-xs uppercase tracking-widest text-[#2A324B]/50 font-semibold">
          {kicker}
        </p>
        {children}
        <div className="mt-auto pt-6">
          <Watermark />
        </div>
      </div>
    </div>
  );

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <div className="w-full max-w-md mx-auto">
      {/* CAROUSEL — horizontal scroll with snap, hidden scrollbar */}
      <div
        ref={scrollRef}
        className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth touch-pan-x"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <style>{`
          .skin-carousel::-webkit-scrollbar { display: none; }
        `}</style>

        {/* ── Card 1: Tone — labeled chips (Primary Tone / Undertone), not merged ── */}
        <CardShell kicker="Your skin tone">
          <div className="flex-1 flex flex-col justify-center">
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <p className="text-[13px] uppercase tracking-widest text-[#2A324B]/55 font-semibold mb-2">Primary tone</p>
                {toneInfo.tone ? (
                  <span className="inline-block px-4 py-2 bg-[#F3D5B5] text-[#2A324B] rounded-full text-xl font-bold">
                    {toneInfo.tone}
                  </span>
                ) : (
                  <span className="inline-block px-4 py-2 bg-gray-100 text-gray-400 rounded-full text-sm italic">
                    Not detected
                  </span>
                )}
              </div>
              <div>
                <p className="text-[13px] uppercase tracking-widest text-[#2A324B]/55 font-semibold mb-2">Undertone</p>
                {toneInfo.undertone ? (
                  <span className="inline-block px-4 py-2 border-2 border-[#F3D5B5] text-[#2A324B] rounded-full text-xl font-bold">
                    {toneInfo.undertone}
                  </span>
                ) : (
                  <span className="inline-block px-4 py-2 border-2 border-gray-200 text-gray-400 rounded-full text-sm italic">
                    Not detected
                  </span>
                )}
              </div>
            </div>
            {toneInfo.description && (
              <p className="mt-6 text-sm text-[#2A324B]/70 leading-relaxed">
                {firstSentence(toneInfo.description)}
              </p>
            )}
          </div>
        </CardShell>

        {/* ── Card 2: Type — clean, no unfinished pill row, inline confidence ── */}
        <CardShell kicker="Your skin type">
          <div className="flex-1 flex flex-col justify-center">
            <h2 className="text-4xl sm:text-5xl font-bold text-[#2A324B] tracking-tight leading-tight mt-4">
              {skinTypeInfo.type || 'Analysed'}
            </h2>
            {confidenceLabel && (
              <p className="mt-3 text-xs text-[#2A324B]/60">
                Confidence · <span className="font-semibold text-[#2A324B]">{confidenceLabel}</span>
              </p>
            )}
            {skinTypeInfo.characteristics.length > 0 && (
              <p className="mt-5 text-sm text-[#2A324B]/70 leading-relaxed">
                {firstSentence(skinTypeInfo.characteristics[0])}
              </p>
            )}
            {skinTypeInfo.characteristics.length > 1 && (
              <CardExpander
                summary={`See ${skinTypeInfo.characteristics.length} observations`}
                className="mt-5"
              >
                <ul className="space-y-1.5 mt-2">
                  {skinTypeInfo.characteristics.map((c, i) => (
                    <li key={i} className="text-sm text-[#2A324B]/75 flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#F3D5B5] mt-2 flex-shrink-0" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </CardExpander>
            )}
          </div>
        </CardShell>

        {/* ── Card 3: Concerns (kept — Sofia liked this beat) ── */}
        <CardShell kicker={
          concerns.length === 0
            ? 'No concerns detected'
            : `I noticed ${concerns.length} ${concerns.length === 1 ? 'thing' : 'things'} to watch`
        }>
          <div className="flex-1 flex flex-col justify-center mt-4">
            {concerns.length === 0 ? (
              <h2 className="text-3xl sm:text-4xl font-bold text-[#2A324B] tracking-tight leading-tight">
                Skin looks balanced.
              </h2>
            ) : (
              <div className="space-y-2.5">
                {concerns.map((c, i) => (
                  <ConcernPill key={i} concern={c} severityColor={severityColor(c.severity)} />
                ))}
                <p className="text-[13px] text-[#2A324B]/50 mt-3">
                  Tap a concern to see why.
                </p>
              </div>
            )}
          </div>
        </CardShell>
      </div>

      {/* DOTS */}
      <div className="flex justify-center gap-1.5 mt-4">
        {[0, 1, 2].map((i) => (
          <button
            key={i}
            onClick={() => scrollToCard(i)}
            aria-label={`Card ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-200 ${
              activeIndex === i ? 'w-6 bg-[#2A324B]' : 'w-1.5 bg-[#2A324B]/20'
            }`}
          />
        ))}
      </div>

      {/* CTAs — two square cards (no longer pills) */}
      <div className="mt-6 space-y-3">
        <p className="text-[13px] uppercase tracking-widest text-[#2A324B]/45 font-semibold text-center">
          What you can do next
        </p>
        <div className="grid grid-cols-2 gap-3">
          {onSeeShadeMatches && (
            <CtaSquareB
              label="See shade matches"
              sub="Foundations for your tone"
              onClick={onSeeShadeMatches}
              variant="primary"
              iconType="palette"
            />
          )}
          {onAnalyseProduct && (
            <CtaSquareB
              label="Analyse a product"
              sub="Scan to check fit"
              onClick={onAnalyseProduct}
              variant="secondary"
              iconType="scan"
            />
          )}
        </div>
        {hasRoutine === false && (
          <button
            type="button"
            onClick={handleAddRoutine}
            className="w-full text-center text-xs text-[#2A324B]/60 hover:text-[#2A324B] underline underline-offset-4 decoration-[#2A324B]/30 pt-1"
          >
            Add your routine for fully tailored advice  →
          </button>
        )}
      </div>

      {/* Disclaimer (small, single line) — no more redundant Trust & detail block,
          since each card already carries its own detail via expander. */}
      {sections.disclaimer && (
        <p className="text-[13px] text-[#2A324B]/45 italic leading-relaxed text-center pt-4">
          {stripMarkdown(sections.disclaimer)}
        </p>
      )}
    </div>
  );
};

// ── ConcernPill (inline component) ────────────────────────────────────────────
// Tap to expand: severity dot + concern title + caret. When expanded, shows
// the description below. Replaces the heavy "concern card with severity badge"
// pattern from the legacy render.
const ConcernPill = ({ concern, severityColor }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-[#F2EAE1]/40 rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#F2EAE1]/70 transition-colors"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${severityColor}`} />
          <span className="text-sm font-medium text-[#2A324B] truncate">
            {concern.title}
          </span>
        </div>
        <ChevronDown
          className={`w-3.5 h-3.5 text-[#2A324B]/40 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1">
          <p className="text-xs text-[#2A324B]/75 leading-relaxed">
            {concern.description}
          </p>
        </div>
      )}
    </div>
  );
};

// ── CardExpander (inline) ─────────────────────────────────────────────────────
// Used inside a carousel card to reveal extra observations without leaving the
// card. Different from the legacy "Trust & detail" block — this lives WITHIN
// the relevant card, so the detail is co-located with what it explains.
const CardExpander = ({ summary, children, className = '' }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between text-xs text-[#2A324B]/70 hover:text-[#2A324B] py-1"
      >
        <span className="font-medium">{summary}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && children}
    </div>
  );
};

// ── CtaSquareB (inline) ───────────────────────────────────────────────────────
// Square CTA card with icon top-left + label + sub-line. Replaces the thin pill
// CTAs Sofia called out as too lightweight. Used below the carousel.
const CtaSquareB = ({ label, sub, onClick, variant, iconType }) => {
  const primary = variant === 'primary';
  const IconCmp = iconType === 'palette' ? Palette : Scan;
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        primary
          ? 'bg-[#2A324B] text-[#F3D5B5] rounded-2xl p-4 flex flex-col items-start gap-2.5 text-left hover:bg-[#2A324B]/90 active:scale-[0.98] transition-all shadow-sm min-h-[100px]'
          : 'bg-white text-[#2A324B] border-2 border-[#2A324B] rounded-2xl p-4 flex flex-col items-start gap-2.5 text-left hover:bg-[#F2EAE1] active:scale-[0.98] transition-all min-h-[100px]'
      }
    >
      <IconCmp className={`w-5 h-5 ${primary ? 'text-[#F3D5B5]' : 'text-[#2A324B]'}`} />
      <div>
        <p className={`text-sm font-semibold ${primary ? 'text-[#F3D5B5]' : 'text-[#2A324B]'}`}>{label}</p>
        <p className={`text-[13px] mt-0.5 leading-snug ${primary ? 'text-[#F3D5B5]/70' : 'text-[#2A324B]/60'}`}>{sub}</p>
      </div>
    </button>
  );
};

export default SkinAnalysisCard;
